import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import GreetingHeader from "../../components/dashboard/GreetingHeader";
import StatCard from "../../components/dashboard/StatCard";
import EmptyHint from "../../components/dashboard/EmptyHint";
import QuickAction from "../../components/dashboard/QuickAction";
import MonthlyReportButton from "../../components/dashboard/MonthlyReportButton";
import TerritoryLabel from "../../components/common/TerritoryLabel";
import {
  num, pctLabel, count, plural, joinParts, NO_VALUE,
  NO_VISIT_RECORDED, NOT_REPORTED,
} from "../../lib/vocabulary";
import { teamCountLabel } from "../../lib/population";
import { formatISTDateTime, formatISTDate } from "../../lib/datetime";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Plus,
  Users,
  Stethoscope,
  ClipboardList,
  CheckCircle2,
  FileBarChart,
  CalendarClock,
  UserPlus,
  Activity,
  Inbox,
  Receipt,
  CalendarOff,
  Package,
  Truck,
} from "lucide-react";

// The scope word for a figure, or null when the response does not name a scope
// we recognise.
//
// Null rather than a default, deliberately. Cloudflare previews run this build
// against the PRODUCTION backend, so during a deploy window the response can
// still be the old shape — `scope: "team"` over reports-only figures. Guessing
// "Team" there would be right by luck; guessing it over a combined figure is
// the mislabel this issue exists to remove. An unlabelled number is a number
// you have to ask about. A wrongly labelled one is a number you act on.
const SCOPE_WORDS = {
  own_and_team: "You + team",
  company: "Company",
};

// `num()` and the null-percentage rule were PROMOTED OUT of this file into
// `lib/vocabulary.js` — every dashboard now shares them. The local copies are
// deleted rather than left beside the import (Pattern C): an unused private
// helper is what the next edit forks.

export default function AssistantManagerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // The own-work and activity panels below. Built from the EXISTING list
  // endpoints the My/Team surfaces already read — no new backend.
  const [own, setOwn] = useState(null);
  const [ownError, setOwnError] = useState("");
  const [feed, setFeed] = useState(null);
  const [feedError, setFeedError] = useState("");

  // STOCK AND DISTRIBUTION (F88 / F288). Its own request, because it is its own
  // module and `/dashboard/assistant_manager` is another block's file — and
  // because a stock figure that fails to load must leave the stock tile blank
  // rather than take the visit tiles down with it.
  const [stock, setStock] = useState(null);
  const [stockError, setStockError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/assistant_manager");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) {
          setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const userId = user?.id;
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    // `so_id` is the same narrowing the My/Team toggle applies; without it a
    // manager's unscoped list is his whole team's, and an own-work panel built
    // from it would count his reports' claims as his.
    Promise.all([
      api.get("/expenses", { params: { so_id: userId, status: "Submitted", limit: 50 } }),
      api.get("/leaves", { params: { so_id: userId, status: "pending", limit: 50 } }),
    ])
      .then(([e, l]) => {
        if (!cancelled) setOwn({ expenses: e.data || [], leaves: l.data || [] });
      })
      .catch((err) => {
        if (!cancelled) {
          setOwnError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
      });
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    // `GET /visits` is already scoped by the backend's own visibility filter —
    // reports-plus-self for a Sales Manager, company-wide for an Admin Manager
    // — which is why the feed needed no new endpoint.
    // "/doctor-visits", the router's real prefix. This said "/visits" — a URL
    // the backend has NEVER served — and the feed 404ed on every load while
    // the suite stayed green, because the test mocked the same wrong URL. The
    // unwired-mock defect from CLAUDE.md, caught live by the Block 5 sweep
    // (Pankaj NEW-2). Mock every request the unit issues — at the path the
    // backend actually serves.
    api.get("/doctor-visits", { params: { limit: 8 } })
      .then(({ data }) => { if (!cancelled) setFeed(data || []); })
      .catch((err) => {
        if (!cancelled) {
          setFeedError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get("/stock/summary")
      .then(({ data }) => { if (!cancelled) setStock(data); })
      .catch((err) => {
        if (!cancelled) {
          setStockError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const s = data?.stats || {};
  // "company" for an Admin Manager, "own_and_team" for a Sales Manager. The
  // backend decides and says so; this file never inspects the role. Every
  // activity tile below counts over that same population and its denominator
  // moves with it, so each one carries the scope on its face — a number whose
  // meaning depends on who is logged in has to announce it, or two people
  // compare notes and one of them is silently wrong.
  const companyWide = data?.scope === "company";
  const scopeWord = SCOPE_WORDS[data?.scope] ?? null;
  const suffix = (tail) => (scopeWord ? `${scopeWord} · ${tail}` : tail);

  // null, not 0, when either input is missing — now from `lib/vocabulary`.
  const completion = pctLabel(s.visits_completed_today, s.visits_planned_today);

  // B6 through ONE implementation. The backend ships `team_size`,
  // `team_reports` and `team_includes_manager` together precisely so no surface
  // recomputes the split; `teamCountLabel` is the only thing that turns them
  // into words.
  //
  // The COMPANY branch used to say a flat "Company-wide", which asserted
  // nothing about who was in the number — and the number is FIELD_ROLES, so an
  // Admin Manager reading it is not in his own headcount. `team_includes_manager`
  // is false there and the label follows it instead of a role check.
  const headcountHelper = loading
    ? "…"
    : teamCountLabel({
        total: s.team_size,
        reports: s.team_reports,
        includesManager: s.team_includes_manager,
        noun: companyWide ? "field staff · Company-wide" : "in your team",
      }) ?? NO_VALUE;

  // "My Doctors" is hidden for an Admin Manager rather than shown reading 0.
  // `_validate_assignments` refuses `assigned_manager_id` for anyone but a Sales
  // Manager, so his count is structurally zero — and a permanent 0 reads as a
  // book he has lost, not a slot he was never eligible for. The `> 0` arm is
  // not defensive padding: `doctor_loader._assignment()` DOES accept
  // `admin_manager`, so the two paths disagree, and a book that exists must
  // never become invisible because the UI assumed it could not.
  const showMyDoctors = !companyWide || s.my_doctors > 0;

  // STOCK (P4). The tile counts units PLACED at doctors' premises — Callisto's
  // property, at a different address. It is deliberately not called "on hand":
  // nothing in this system records a return, a consumption or a count at the
  // premises, so what is still there is not derivable and the backend refuses
  // to emit a number for it.
  const atDoctors = stock?.at_doctors;
  const stockLoading = stock === null && !stockError;
  const stockValue = atDoctors?.value;

  // VALUE, OR THE REASON THERE IS NONE. Never "₹0" — a stock position worth
  // nothing is a claim about the balance sheet, and it is the claim a reader
  // makes from a zero. D5/D6 cost prices are outstanding, so today this reads
  // as the refusal, and it starts reading as money on the day they land.
  const stockHelper = stockValue
    ? stockValue.computable
      ? `Callisto-owned · ₹${formatINR(stockValue.rupees)} at ${stockValue.valued_by}`
      : "Callisto-owned · value not computable"
    : "Callisto-owned, at doctors' premises";

  // The named inputs behind every refusal on this page, gathered from the
  // response rather than written here — the surface must say WHICH value is
  // absent, and only the module knows.
  const stockGaps = [
    ...(atDoctors?.on_hand?.computable === false ? atDoctors.on_hand.missing : []),
    ...(stockValue?.computable === false ? stockValue.missing : []),
    ...(stock?.at_distributors?.computable === false
      ? stock.at_distributors.missing
      : []),
  ];

  // RC-23, and D11 decides who sees it: `distributor_coverage` is absent for a
  // sales role because the punch list behind the tile refuses them. A tile
  // nobody can act on is the inert tile RC-15 is about, so it is not rendered
  // rather than rendered dead.
  const coverage = stock?.distributor_coverage;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <GreetingHeader
        user={user}
        subtitle="Your book, your own claims and your team at a glance. Drill into any tile to take action."
      />

      {error && (
        <div
          className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2"
          data-testid="dashboard-error"
        >
          Dashboard figures did not load — {error}
        </div>
      )}

      {/* Managers log their own calls for the doctors assigned to them (§1 Fix
          A). Same tile as the officer dashboard, same destination; the server
          refuses any doctor that is not theirs. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <QuickAction
          to="/doctor-visits/new"
          Icon={Plus}
          label="Add Visit"
          helper="Log a call on a doctor assigned to you"
          testId="quick-action-add-visit"
        />
      </div>

      {/* The one-button monthly report — the same control on every dashboard. */}
      <MonthlyReportButton />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Two headcount tiles, because they answer different questions. A Sales
            Manager holds doctors directly AND carries his reps' achievement on
            theirs; one number cannot say both, and the single "My Team: 0" an IC
            manager used to get told him nothing about a book of fifteen. */}
        {showMyDoctors && (
          <StatCard
            testId="stat-my-doctors"
            label="My Doctors"
            value={num(loading, s.my_doctors)}
            helper="Assigned to you directly"
            Icon={Stethoscope}
            // Opens the list pre-filtered to the same set this tile counted —
            // both go through the backend's `owned_doctor_query`.
            onClick={() => navigate(`/doctors?assigned_to=${user?.id}`)}
            loading={loading}
          />
        )}
        <StatCard
          testId="stat-team-size"
          label={companyWide ? "Field Staff" : "My Team"}
          value={num(loading, s.team_size)}
          helper={headcountHelper}
          Icon={Users}
          onClick={() => navigate("/team")}
          loading={loading}
        />

        {/* The four activity tiles show ONE combined figure — the manager's own
            records and his reports'. The split is a drill-down, not a tile face:
            he is accountable for the total, and reading two half-numbers off a
            dashboard is how the halves get compared instead of added. */}
        <StatCard
          testId="stat-team-planned"
          label="Visits Planned"
          value={num(loading, s.visits_planned_today)}
          helper={suffix("today")}
          Icon={ClipboardList}
          onClick={() => navigate("/route-planner")}
          loading={loading}
        />
        <StatCard
          testId="stat-team-completed"
          label="Visits Completed"
          value={num(loading, s.visits_completed_today)}
          helper={suffix(completion === NO_VALUE ? "today" : `${completion} of plan`)}
          Icon={CheckCircle2}
          accent
          onClick={() => navigate("/doctor-visits")}
          loading={loading}
        />
        <StatCard
          testId="stat-team-dcr"
          label="DCR Submission"
          value={pctLabel(s.dcr_rate_month, 1)}
          // The head count is on the tile because the denominator IS the head
          // count: the same percentage is per-team for one viewer and
          // per-company for another, and an IC manager's is over himself alone.
          helper={suffix(
            s.counted_people === undefined || s.counted_people === null
              ? "this month"
              : `${plural(s.counted_people, "person", "people")}, this month`
          )}
          Icon={FileBarChart}
          onClick={() => navigate("/analytics")}
          loading={loading}
        />
        {/* Carries no scope word, because its count does not move with one:
            `pending_counts` is an inbox and is already role-aware. Labelling it
            alongside the others would imply it combined too; it was always
            complete. */}
        <StatCard
          testId="stat-approvals-pending"
          label="Approvals Pending"
          value={num(loading, s.approvals_pending_count)}
          helper="Expenses, leaves and tour plans"
          Icon={CalendarClock}
          onClick={() => navigate("/approvals")}
          loading={loading}
        />

        {/* STOCK AND DISTRIBUTION — the tiles the audit reserved (F88, F288).
            Both manager tiers get the stock tile: a Sales Manager carries
            samples of his own and his reps', and the Admin Manager is the
            company custodian. The scope word comes from the stock response's
            own `scope`, not from this one's, because the two are separate
            requests and a label taken from the wrong one is the RC-9 mislabel
            with extra steps. */}
        <StatCard
          testId="stat-stock-at-doctors"
          label="Stock at Doctors"
          value={num(stockLoading, atDoctors?.units_placed)}
          helper={stockHelper}
          Icon={Package}
          onClick={() => navigate("/samples")}
          loading={stockLoading}
        />
        {coverage && (
          <StatCard
            testId="stat-distributor-gap"
            label="Distributors Unplaced"
            value={num(stockLoading, coverage.visibility_gap)}
            helper={`of ${count(coverage.active_total)} active · visible to no officer`}
            Icon={Truck}
            onClick={() => navigate("/distributors?needs_territory=1")}
            loading={stockLoading}
          />
        )}
      </div>

      {stockError && (
        <div
          className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2"
          data-testid="stock-error"
        >
          Stock and distribution figures did not load — {stockError}
        </div>
      )}

      {/* WHAT IS NOT COMPUTABLE, AND WHY, ON THE SURFACE. A tile that quietly
          omits a figure teaches the reader the figure is zero. The sentences
          are the module's, verbatim, so the named input is the one the backend
          is actually waiting for. */}
      {stockGaps.length > 0 && (
        <div
          className="rounded-md border border-callisto bg-callisto-app/60 px-3 py-2"
          data-testid="stock-gaps"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wider text-callisto-muted">
            Not computable yet
          </p>
          <ul className="mt-1 space-y-1">
            {stockGaps.map((m) => (
              <li key={m} className="text-xs text-callisto-body">
                {m}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* MY OWN WORK (RC-15). Both manager tiers file their own claims —
          `SELF_SERVICE_MANAGERS` — and every surface on this page before now
          was about other people. The Admin Manager in particular had no
          own-work section anywhere on his dashboard, and the two panels below
          read the same `/expenses` and `/leaves` lists the "My" tab already
          serves.

          TOUR PLANS ARE DELIBERATELY ABSENT. `TourPlansRouter` gives an Admin
          Manager the team view only — he oversees plans and never files one, so
          an own-plan panel would be structurally empty for exactly the role
          this finding is about. */}
      <Card className="bg-white border-callisto shadow-sm rounded-xl">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base text-callisto-navy flex items-center gap-2">
            <Inbox className="h-4 w-4 text-callisto-gold" />
            My own claims
          </CardTitle>
          <span className="text-[11px] uppercase tracking-wider text-callisto-muted">
            Waiting on an approver
          </span>
        </CardHeader>
        <CardContent className="pt-0">
          {ownError ? (
            <p
              className="text-sm text-red-700 px-3 py-2"
              data-testid="own-work-error"
            >
              Your own claims did not load — {ownError}
            </p>
          ) : own === null ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="own-work">
              <OwnWorkPanel
                testId="own-work-expenses"
                Icon={Receipt}
                title="My expenses"
                rows={own.expenses}
                empty="No expense of yours is awaiting a decision."
                onOpen={() => navigate("/expenses")}
                renderRow={(r) => ({
                  key: r.id,
                  primary: joinParts([r.expense_type, `₹${formatINR(r.amount)}`]),
                  secondary: formatISTDate(r.expense_date),
                })}
              />
              <OwnWorkPanel
                testId="own-work-leaves"
                Icon={CalendarOff}
                title="My leaves"
                rows={own.leaves}
                empty="No leave of yours is awaiting a decision."
                onOpen={() => navigate("/leaves")}
                renderRow={(r) => ({
                  key: r.id,
                  primary: joinParts([
                    r.leave_type,
                    r.days_count === undefined || r.days_count === null
                      ? null
                      : plural(r.days_count, "day"),
                  ]),
                  secondary: joinParts(
                    [formatISTDate(r.from_date), formatISTDate(r.to_date)],
                    " – "
                  ),
                })}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-callisto shadow-sm rounded-xl">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base text-callisto-navy flex items-center gap-2">
            <Users className="h-4 w-4 text-callisto-gold" />
            {companyWide ? "Field staff" : "My team"}
          </CardTitle>
          <span className="text-[11px] uppercase tracking-wider text-callisto-muted">
            Today's status
          </span>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : data?.team_members?.length ? (
            <ul className="divide-y divide-callisto" data-testid="team-members-list">
              {data.team_members.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="h-9 w-9 shrink-0 rounded-full bg-callisto-navy text-white text-xs font-semibold flex items-center justify-center">
                      {initials(m.name)}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-callisto-navy truncate">
                        {m.name}
                      </p>
                      <TerritoryLabel
                        value={m.territory}
                        unmigrated={m.territory_unmigrated}
                        review="icon"
                        className="text-[11px] text-callisto-muted uppercase tracking-wider truncate block"
                      />
                    </div>
                  </div>
                  <StatusPill status={m.status} />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyHint
              Icon={UserPlus}
              title="No reports yet"
              hint="Once a Sales Officer is assigned to you as their manager, they'll appear here with their daily status."
              cta={{ to: "/team", label: "View team" }}
              testId="team-members-empty"
            />
          )}
        </CardContent>
      </Card>

      {/* ACTIVITY FEED (RC-15, "missing activity feed for SM"). No new
          endpoint: `GET /visits` is the same list the Team Visits page reads,
          and its visibility filter already draws the manager's population. */}
      <Card className="bg-white border-callisto shadow-sm rounded-xl">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base text-callisto-navy flex items-center gap-2">
            <Activity className="h-4 w-4 text-callisto-gold" />
            Recent activity
          </CardTitle>
          <span className="text-[11px] uppercase tracking-wider text-callisto-muted">
            {scopeWord ? `${scopeWord} · last 8 visits` : "Last 8 visits"}
          </span>
        </CardHeader>
        <CardContent className="pt-0">
          {feedError ? (
            <p className="text-sm text-red-700 px-3 py-2" data-testid="activity-feed-error">
              Recent activity did not load — {feedError}
            </p>
          ) : feed === null ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : feed.length ? (
            <ul className="space-y-2" data-testid="activity-feed">
              {feed.map((v) => (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/doctor-visits/${v.id}`)}
                    className="w-full text-left flex items-start gap-3 px-3 py-2 rounded-md hover:bg-callisto-app focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-callisto-gold"
                  >
                    <div className="h-2 w-2 rounded-full bg-callisto-gold mt-2" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-callisto-body">
                        {joinParts(
                          [v.sales_officer_name, `visited ${v.doctor_name || NO_VALUE}`],
                          " "
                        )}
                      </p>
                      <p className="text-[11px] text-callisto-muted">
                        {formatISTDateTime(v.submitted_at || v.visit_date)}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyHint
              Icon={Activity}
              title="No visits logged yet"
              hint="As your team files DCRs, the most recent ones appear here."
              cta={{ to: "/doctor-visits", label: "Open visits" }}
              testId="activity-feed-empty"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * One of the manager's own claim lists. `rows` is what the API returned, so an
 * empty array is a KNOWN empty — the panel says so in words rather than
 * printing a 0 that could equally mean the request failed.
 */
function OwnWorkPanel({ testId, Icon, title, rows, empty, onOpen, renderRow }) {
  const total = rows?.length ?? null;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={testId}
      className="text-left rounded-lg border border-callisto p-3 transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-callisto-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-callisto-gold"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-callisto-navy">
          <Icon className="h-4 w-4 text-callisto-gold" />
          {title}
        </span>
        <span className="text-sm font-bold text-callisto-navy">{count(total)}</span>
      </div>
      {total ? (
        <ul className="mt-2 space-y-1">
          {rows.slice(0, 3).map((r) => {
            const row = renderRow(r);
            return (
              <li key={row.key} className="text-[11px] text-callisto-muted truncate">
                {joinParts([row.primary, row.secondary])}
              </li>
            );
          })}
          {rows.length > 3 && (
            <li className="text-[11px] text-callisto-muted">
              +{rows.length - 3} more
            </li>
          )}
        </ul>
      ) : (
        <p className="mt-2 text-[11px] text-callisto-muted">{empty}</p>
      )}
    </button>
  );
}

function formatINR(n) {
  if (n === null || n === undefined) return NO_VALUE;
  try {
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);
  } catch {
    return String(n);
  }
}

function initials(name) {
  return (name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/**
 * Pattern A (RC-11). "Not visited" is a claim; "No visit recorded today" is
 * what the database actually knows, and it is the wording `lib/vocabulary`
 * promoted from the Field Map. The `|| map.not_visited` fallback was the
 * second half of the same defect: a status this build does not recognise was
 * painted amber and labelled as a rep who had not worked.
 */
function StatusPill({ status }) {
  const map = {
    visited: {
      label: "Visited today",
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      dot: "bg-emerald-500",
    },
    not_visited: {
      label: NO_VISIT_RECORDED,
      bg: "bg-amber-50",
      text: "text-amber-700",
      dot: "bg-amber-500",
    },
    on_leave: {
      label: "On leave",
      bg: "bg-slate-100",
      text: "text-slate-600",
      dot: "bg-slate-400",
    },
  };
  const v = map[status] || {
    label: NOT_REPORTED,
    bg: "bg-slate-100",
    text: "text-slate-600",
    dot: "bg-slate-400",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 ${v.bg} ${v.text} text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${v.dot}`} />
      {v.label}
    </span>
  );
}
